/**
 * STMIDB Equipment Service
 *
 * Provides access to equipment data from the stmidb customer project.
 * Data is fetched via Foreign Data Wrapper and mapped to company
 * c4960d35-4a4e-461a-a669-dfae1bed5ec4 in the app.
 *
 * Any updates made through this service sync back to the customer's database.
 */

import { supabase } from '@/lib/supabase/client'

// =============================================================================
// TYPES
// =============================================================================

/** STMIDB row reshaped to the equipment_v2 columns the inspector UI reads. */
export interface STMIDBMappedEquipment {
  id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
  category_code: string | null
  category_title: string
  company_id: string
  created_at: string
}

export interface STMIDBEquipment {
  id: string
  asset_number: string | null
  serial_number: string | null
  part_number: string | null
  description: string | null
  category: string | null
  equipment_type: string | null
  status: string | null
  equipment_status: string | null // Inspection status: OK, C, MO, RC, NC, EXP
  equipment_code: string | null
  last_inspection_date: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  last_5y_date: string | null
  next_5y_date: string | null
  last_10y_date: string | null
  next_10y_date: string | null
  commission_date: string | null
  retirement_date: string | null
  owner_name: string | null
  created_at: string
  updated_at: string
}

export interface STMIDBAttachableEquipment {
  id: string
  asset_number: string | null
  serial_number: string | null
  part_number: string | null
  description: string | null
  category: string | null
  equipment_type: string | null
  equipment_code: string | null
}

export interface STMIDBReportEquipmentHit extends STMIDBAttachableEquipment {
  parent_equipment_id: string | null
}

export interface STMIDBChildEquipment {
  id: string
  parent_equipment_id: string
  serial_number: string | null
  category: string | null
  equipment_type: string | null
}

export interface STMIDBEquipmentFilters {
  status?: string
  category?: string
  search?: string
  limit?: number
  offset?: number
}

/** TechnipFMC workshop service order from stmidb.service_orders. */
export interface STMIDBServiceOrder {
  id: number
  so: string
  location: string
  part_number: string | null
  serial_number: string | null
  submitted_by: string | null
  needed_by: string | null
  created_at: string
  status: string | null
  updated_at: string | null
  result: string | null
  comment: string | null
  performed: string[] | null
}

/** File in the stmidb `tfmc` bucket, keyed by service order number. */
export interface STMIDBServiceOrderFile {
  so: string
  file_name: string
  file_path: string
  created_at: string | null
  document_kind: 'control_card' | 'service_report' | 'ndt' | 'other'
}

export type STMIDBEquipmentUpdate = Partial<
  Omit<STMIDBEquipment, 'id' | 'created_at' | 'updated_at' | 'owner_name'>
>

// STMIDB Document interface
export interface STMIDBDocument {
  id: string
  equipment_id: string
  document_type: string | null // co, ndt, cert, coc, man, sr
  file_name: string | null
  file_path: string | null
  created_at: string
  updated_at: string
}

/**
 * Maps STMIDB document type to WLL document type
 * STMIDB: co, ndt, cert, coc, man, sr, 5_year
 * WLL: control_card, certificate, declaration_of_conformity, ndt_report, service_report, user_manual, other
 */
export const mapSTMIDBDocumentTypeToWLL = (stmidbType: string | null): string => {
  if (!stmidbType) return 'other'

  const typeMap: Record<string, string> = {
    co: 'control_card',
    cert: 'certificate',
    coc: 'declaration_of_conformity',
    ndt: 'ndt_report',
    sr: 'service_report',
    man: 'user_manual',
    '5_year': 'ndt_report', // closest WLL equivalent for 5-year inspection reports
  }

  return typeMap[stmidbType.toLowerCase()] || 'other'
}

/**
 * True when an STMIDB document row is just a `.keep` folder placeholder and
 * should never be shown in the ECM UI. STMIDB inserts these so the
 * `documentation` bucket keeps empty `co/`, `ndt/`, `coc/`, `man/`, etc.
 * folders per equipment. They have `file_name = '.keep'` and paths ending in
 * `/.keep`, and trying to open them yields an empty PDF.
 */
export const isSTMIDBPlaceholderDocument = (doc: {
  file_name?: string | null
  file_path?: string | null
}): boolean => {
  const name = (doc.file_name || '').trim().toLowerCase()
  const path = (doc.file_path || '').trim().toLowerCase()
  if (name === '.keep') return true
  if (path.endsWith('/.keep') || path === '.keep') return true
  return false
}

/**
 * Maps a WLL category code (e.g. "F1-1", "R4-2", "R10-3") to the STMIDB
 * `equipment.category` text column (e.g. "F1", "R4", "SDLA"). STMIDB uses
 * top-level category codes only — subcategory information lives in
 * `equipment_type` on that side. Returns null when no sensible mapping
 * exists so the caller can surface a validation error instead of creating a
 * row with a garbage category.
 */
export const mapWLLCategoryCodeToSTMIDB = (
  wllCode: string | null | undefined
): string | null => {
  if (!wllCode) return null
  const trimmed = wllCode.trim().toUpperCase()
  if (!trimmed) return null

  // Strip subcategory suffix: "F1-1" -> "F1", "R10-3" -> "R10".
  const base = trimmed.split('-')[0]

  // R10 (Specially Designed Lifting Appliances) is labelled "SDLA" on the
  // STMIDB side, not "R10". Everything else round-trips through the
  // existing STMIDB->WLL map, so inverting it gives us the right key.
  if (base === 'R10') return 'SDLA'

  for (const [stmidbKey, wllValue] of Object.entries(STMIDB_CATEGORY_TO_WLL)) {
    if (wllValue === base) return stmidbKey
  }

  // Fallthrough: if the prefix matches STMIDB's own code pattern, just pass
  // it through. WL/F1/F2/R1 etc. already match exactly.
  if (/^[A-Z]+\d*$/.test(base)) return base
  return null
}

/**
 * Maps a WLL document_type (as selected in DocumentUploadModal) back to the
 * STMIDB enum accepted by the upload-document edge function.
 * "other" has no STMIDB equivalent — we fall back to 'co' (control card),
 * which is the most permissive bucket on the SLB side.
 */
export const mapWLLDocumentTypeToSTMIDB = (wllType: string | null): string => {
  if (!wllType) return 'co'
  const typeMap: Record<string, string> = {
    control_card: 'co',
    certificate: 'cert',
    cal_cert: 'cert',
    declaration_of_conformity: 'coc',
    ndt_report: 'ndt',
    thread_report: 'ndt',
    service_report: 'sr',
    user_manual: 'man',
    other: 'co',
  }
  return typeMap[wllType.toLowerCase()] || 'co'
}

// STMIDB uses custom domain db.stmi.no
const STMIDB_API_BASE = 'https://db.stmi.no'

/** STMIDB edge functions verify the caller's WLL session and require admin/inspector. */
const wllAuthHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
}

/**
 * Generates a signed URL for an STMIDB document by calling STMIDB's edge function.
 *
 * Example file_path in DB: schlumberger/SLB_01655/coc/coc_SLB_01655.pdf
 * Returns: A signed URL that expires after the specified duration
 */
export const getSTMIDBDocumentSignedUrl = async (
  filePath: string,
  expiresIn: number = 3600,
  bucket: string = 'documentation',
): Promise<string> => {
  if (!filePath) return ''

  // Remove leading slash if present
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath

  try {
    const response = await fetch(`${STMIDB_API_BASE}/functions/v1/get-document-signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await wllAuthHeaders()),
      },
      body: JSON.stringify({
        file_path: cleanPath,
        expires_in: expiresIn,
        bucket,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Failed to get signed URL:', errorData)
      return ''
    }

    const data = await response.json()
    return data.signed_url || ''
  } catch (error) {
    console.error('Error getting STMIDB document signed URL:', error)
    return ''
  }
}

/**
 * @deprecated Use getSTMIDBDocumentSignedUrl instead for secure access
 * Constructs a public URL (only works if bucket has public access enabled)
 */
export const getSTMIDBDocumentPublicUrl = (filePath: string): string => {
  if (!filePath) return ''
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath
  return `${STMIDB_API_BASE}/storage/v1/object/public/documentation/${cleanPath}`
}

// =============================================================================
// WLL COMPANY MAPPING
// =============================================================================

/**
 * The WLL company ID that stmidb equipment is mapped to.
 * This allows the stmidb equipment to appear alongside other WLL equipment
 * when filtered by this company.
 */
export const STMIDB_WLL_COMPANY_ID = 'c4960d35-4a4e-461a-a669-dfae1bed5ec4'

/**
 * The display name for the STMIDB customer company in the app.
 */
export const STMIDB_WLL_COMPANY_NAME = 'SLB'

/**
 * WLL company that owns rows in stmidb.service_orders.
 * That table is TechnipFMC only — not SLB equipment.
 */
export const STMIDB_SERVICE_ORDERS_COMPANY_ID = 'f1eaef03-ff22-4ffe-904f-deabcc393a70'
export const STMIDB_SERVICE_ORDERS_COMPANY_NAME = 'TechnipFMC'

// =============================================================================
// CATEGORY MAPPING
// =============================================================================

/**
 * Equipment Type to WLL Category Code mapping.
 * These are specific equipment types that map to exact WLL subcategory codes.
 */
const EQUIPMENT_TYPE_TO_WLL_CODE: Record<string, string> = {
  // F1 - Offshore Containers
  'Offshore Container': 'F1-1',
  Basket: 'F1-2',
  Frame: 'F1-3',

  // F2 - Service Containers
  'Service Container': 'F2-1',

  // F3 - Specialized Offshore Units
  'Frame Assembly': 'F3-1',
  'Specialized Offshore Unit': 'F3-2',

  // F4 - Subsea Equipment
  Subsea: 'F4-1',
  'Subsea Equipment': 'F4-1',

  // F5 - Offboard Lifted Objects
  'Offboard Lifted Object': 'F5-1',
  'Lifted Object': 'F5-1',

  // R10 - SDLA (Specially Designed Lifting Appliances)
  'Lifting Cap': 'R10-1',
  'Lifting Nipple': 'R10-2',
  'Lifting Sub': 'R10-3',
  'Lifting Pup-Joint': 'R10-4',
  'Gun Clamp': 'R10-5',
  'Handling Tool': 'R10-6',
  'Running Tool': 'R10-7',
  Elevator: 'R10-8',
  'Powered Elevator': 'R10-9',
  'Elevator Links / Bail': 'R10-10',

  // R4 - Loose Equipment
  'Offshore Shackle': 'R4-2',
  'D-Shackle': 'R4-3',
  'Eye Bolt': 'R4-4',
  'Plate Clamp': 'R4-14',
  'Load Cell': 'R4-15',
  'Beam Clamp': 'R4-11',
  'Eagle Clamp': 'R4-13',
  Shackle: 'R4-1',
  'Master Link': 'R4-5',
  Swivel: 'R4-6',
  Ring: 'R4-7',
  Hook: 'R4-8',
  'Pad Eye': 'R4-9',
  Trunnion: 'R4-10',

  // R5 - Lifting Beams
  'Spreader Beam': 'R5-1',
  'Spreader Frame': 'R5-2',
  'Lifting Beam': 'R5-3',
  'Lifting Frame': 'R5-4',

  // R1 - Wire Rope Slings
  'Wire Rope Sling': 'R1-1',
  'Wire Sling': 'R1-1',

  // R2 - Chain Slings
  'Chain Sling': 'R2-1',

  // R3 - Webbing/Roundslings
  'Webbing Sling': 'R3-1',
  'Round Sling': 'R3-2',
  Roundsling: 'R3-2',

  // G10 - Hand-Operated Cranes
  'Chain Hoist': 'G10-1',
  'Lever Hoist': 'G10-2',
  'Wire Hoist': 'G10-3',

  // FAL - Fall Protection
  'Safety Harness': 'FAL-1',
  'Fall Arrester': 'FAL-5',
  'Fall Block': 'FAL-7',
  Lanyard: 'FAL-2',
  'Anchor Point': 'FAL-3',
  'Self-Retracting Lifeline': 'FAL-4',
  SRL: 'FAL-4',
}

/**
 * STMIDB Category to WLL Category Code mapping.
 * Maps the base category (without Norwegian description suffix).
 */
const STMIDB_CATEGORY_TO_WLL: Record<string, string> = {
  // Direct matches
  F1: 'F1',
  F2: 'F2',
  F3: 'F3',
  F4: 'F4',
  F5: 'F5',
  FAL: 'FAL',
  G10: 'G10',
  G11: 'R4', // G11 in STMIDB = Loose Equipment = R4 in WLL
  G20: 'G20',
  G4: 'G4',
  G7: 'G7',
  R1: 'R1',
  R2: 'R2',
  R3: 'R3',
  R4: 'R4',
  R5: 'R5',
  R6: 'R6',
  R9: 'R9',
  R10: 'R10',
  SDLA: 'R10',

  // STMIDB D-categories map to R10 (SDLA)
  D5: 'R10',
  D6: 'R10',
  D7: 'R10',

  // General/catch-all categories
  RX: 'R4',
  XX: 'XX',
}

/**
 * Extracts the base category code from STMIDB category string.
 * Handles formats like "F1", "F1 - Offshore containere", etc.
 */
const extractBaseCategoryCode = (category: string | null): string | null => {
  if (!category) return null

  const trimmed = category.trim()

  // Extract the code before any " - " separator
  const codeMatch = trimmed.match(/^([A-Z]+\d*(?:-\d+)?)/i)
  if (codeMatch) {
    return codeMatch[1].toUpperCase()
  }

  return trimmed
}

/**
 * Normalizes equipment type string for matching.
 * Handles variations in capitalization, spacing, and special characters.
 */
const normalizeEquipmentType = (type: string): string => {
  return type
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** True when `code` is `family` or one of its subcategories (R10-3 is R10, not R1). */
const matchesCategoryFamily = (code: string | null | undefined, family: string): boolean => {
  if (!code) return false
  const upper = code.trim().toUpperCase()
  return upper === family || upper.startsWith(`${family}-`)
}

const mapEquipmentTypeToWLLCode = (equipmentType: string | null): string | null => {
  const trimmedType = equipmentType?.trim()
  if (!trimmedType) return null

  if (EQUIPMENT_TYPE_TO_WLL_CODE[trimmedType]) {
    return EQUIPMENT_TYPE_TO_WLL_CODE[trimmedType]
  }

  const normalizedInput = normalizeEquipmentType(trimmedType)
  for (const [key, value] of Object.entries(EQUIPMENT_TYPE_TO_WLL_CODE)) {
    if (normalizeEquipmentType(key) === normalizedInput) {
      return value
    }
  }

  for (const [key, value] of Object.entries(EQUIPMENT_TYPE_TO_WLL_CODE)) {
    const normalizedKey = normalizeEquipmentType(key)
    if (normalizedInput.includes(normalizedKey) || normalizedKey.includes(normalizedInput)) {
      return value
    }
  }

  return null
}

/**
 * Maps STMIDB equipment to the corresponding WLL category code.
 * Priority:
 * 1. STMIDB's SDLA classification: "SDLA" (and R10/D5-D7) always lands in
 *    WLL R10, "NON-SDLA" never does. The equipment type only picks the R10
 *    subcategory, or a non-R10 category for NON-SDLA.
 * 2. Equipment type specific mapping (e.g., "Offshore Container" -> "F1-1")
 * 3. Category code mapping (e.g., "F1" -> "F1")
 * 4. Falls back to extracted base code or null
 */
export const mapSTMIDBToWLLCategoryCode = (
  stmidbCategory: string | null,
  equipmentType: string | null
): string | null => {
  const typeCode = mapEquipmentTypeToWLLCode(equipmentType)
  const isSDLAType = matchesCategoryFamily(typeCode, 'R10')

  // extractBaseCategoryCode would reduce "NON-SDLA" to "NON".
  if ((stmidbCategory || '').trim().toUpperCase().startsWith('NON-SDLA')) {
    return typeCode && !isSDLAType ? typeCode : null
  }

  const baseCode = extractBaseCategoryCode(stmidbCategory)
  const categoryCode = baseCode ? STMIDB_CATEGORY_TO_WLL[baseCode] : undefined

  if (categoryCode === 'R10') {
    return isSDLAType ? typeCode : 'R10'
  }

  if (typeCode) return typeCode

  if (baseCode) {
    if (categoryCode) {
      return categoryCode
    }

    if (/^[A-Z]+\d*(-\d+)?$/i.test(baseCode)) {
      return baseCode
    }
  }

  return null
}

/**
 * Gets the WLL category title for a mapped category code.
 * Uses equipment type as title when available, otherwise uses WLL category title.
 */
export const getWLLCategoryTitle = (
  stmidbCategory: string | null,
  wllCategoryCode: string | null,
  equipmentType?: string | null
): string => {
  if (!wllCategoryCode && !stmidbCategory) return 'Uncategorized'

  if (equipmentType && wllCategoryCode) {
    const trimmedType = equipmentType.trim()
    if (EQUIPMENT_TYPE_TO_WLL_CODE[trimmedType] === wllCategoryCode) {
      return trimmedType
    }
    for (const [key, value] of Object.entries(EQUIPMENT_TYPE_TO_WLL_CODE)) {
      if (key.toLowerCase() === trimmedType.toLowerCase() && value === wllCategoryCode) {
        return key
      }
    }
  }

  const WLL_CATEGORY_TITLES: Record<string, string> = {
    F1: 'Offshore Containers',
    'F1-1': 'Offshore Container',
    'F1-2': 'Basket',
    'F1-3': 'Frame',
    F2: 'Service Containers',
    'F2-1': 'Service Container',
    F3: 'Specialized Offshore Units',
    'F3-1': 'Frame Assembly',
    'F3-2': 'Specialized Offshore Unit',
    F4: 'Subsea Equipment',
    'F4-1': 'Subsea',
    F5: 'Offboard Lifted Objects',
    'F5-1': 'Offboard Lifted Object',
    FAL: 'Fall Protection',
    'FAL-1': 'Safety Harness',
    'FAL-5': 'Fall Arrester',
    'FAL-7': 'Fall Block',
    G10: 'Hand-Operated Cranes',
    'G10-1': 'Chain Hoist',
    'G10-2': 'Lever Hoist',
    'G10-3': 'Wire Hoist',
    G20: 'Hydraulic Cranes',
    G4: 'Overhead Cranes',
    G7: 'Powered Winches and Hoists',
    R1: 'Chain Slings',
    R2: 'Fiber Slings',
    R3: 'Wire Slings',
    R4: 'Loose Equipment',
    'R4-2': 'Offshore Shackle',
    'R4-3': 'D-Shackle',
    'R4-4': 'Eye Bolt',
    'R4-11': 'Beam Clamp',
    'R4-13': 'Eagle Clamp',
    'R4-14': 'Plate Clamp',
    'R4-15': 'Load Cell',
    R5: 'Lifting Beams',
    'R5-1': 'Spreader Beam',
    'R5-2': 'Spreader Frame',
    'R5-3': 'Lifting Beam',
    'R5-4': 'Lifting Frame',
    R6: 'Other Equipment',
    R9: 'Lifting Components',
    R10: 'SDLA',
    'R10-1': 'Lifting Cap',
    'R10-2': 'Lifting Nipple',
    'R10-3': 'Lifting Sub',
    'R10-4': 'Lifting Pup-Joint',
    'R10-5': 'Gun Clamp',
    'R10-6': 'Handling Tool',
    'R10-7': 'Running Tool',
    'R10-8': 'Elevator',
    'R10-9': 'Powered Elevator',
    'R10-10': 'Elevator Links / Bail',
    XX: 'Other',
  }

  if (wllCategoryCode && WLL_CATEGORY_TITLES[wllCategoryCode]) {
    return WLL_CATEGORY_TITLES[wllCategoryCode]
  }

  return stmidbCategory || 'Uncategorized'
}

// =============================================================================
// EQUIPMENT MAPPER FUNCTIONS
// =============================================================================

/**
 * STMIDB "PK OK" -> WLL "OK"; other codes pass through. Writes are mapped
 * back to STMIDB names by map_wll_status_to_stmidb in update_stmidb_equipment.
 */
export const mapSTMIDBInspectionStatus = (equipmentStatus: string | null): string | null => {
  if (!equipmentStatus) return null
  const s = equipmentStatus.toUpperCase().trim()
  if (s === 'PK OK' || s === 'OK') return 'OK'
  const validStatuses = ['C', 'MO', 'RC', 'NC', 'EXP', 'M', 'EOL']
  return validStatuses.includes(s) ? s : null
}

/**
 * Maps an STMIDB row to the equipment_v2 shape used by the inspector UI.
 * `categoryIdByCode` resolves the mapped WLL category code to categories_v2.id
 * so spec templates load the same way as for WLL equipment.
 */
export const mapSTMIDBToWLLEquipment = (
  stmidb: Pick<STMIDBEquipment, 'id' | 'asset_number' | 'serial_number' | 'part_number' | 'description' | 'category' | 'equipment_type' | 'status' | 'equipment_status' | 'equipment_code' | 'last_inspection_date' | 'next_inspection_date' | 'last_ndt_date' | 'next_ndt_date' | 'created_at'>,
  categoryIdByCode: Record<string, string> = {},
): STMIDBMappedEquipment => {
  const wllCategoryCode = mapSTMIDBToWLLCategoryCode(stmidb.category, stmidb.equipment_type)
  const wllCategoryTitle = getWLLCategoryTitle(stmidb.category, wllCategoryCode, stmidb.equipment_type)

  return {
    id: stmidb.id,
    name: stmidb.description || stmidb.equipment_type || stmidb.asset_number || 'Unknown',
    asset_number: stmidb.asset_number || '',
    serial_number: stmidb.serial_number,
    part_number: stmidb.part_number,
    // Surface STMIDB's `equipment_code` as "Owner ID" — that's the
    // human-readable identifier SLB users search by.
    owner_id: stmidb.equipment_code || null,
    last_inspection_date: stmidb.last_inspection_date,
    next_inspection_date: stmidb.next_inspection_date,
    last_ndt_date: stmidb.last_ndt_date,
    next_ndt_date: stmidb.next_ndt_date,
    status: stmidb.status || 'active',
    inspection_status: mapSTMIDBInspectionStatus(stmidb.equipment_status),
    description: stmidb.description,
    model: stmidb.equipment_type,
    manufacture_year: null,
    category_id: (wllCategoryCode && categoryIdByCode[wllCategoryCode]) || null,
    category_code: wllCategoryCode,
    category_title: wllCategoryTitle,
    company_id: STMIDB_WLL_COMPANY_ID,
    created_at: stmidb.created_at,
  }
}

/** Resolve WLL category codes (e.g. "R4-2") to categories_v2 ids. */
export const resolveWLLCategoryIds = async (
  codes: Array<string | null | undefined>,
): Promise<Record<string, string>> => {
  const unique = Array.from(new Set(codes.filter((c): c is string => !!c)))
  if (unique.length === 0) return {}
  const { data } = await supabase.from('categories_v2').select('id, code').in('code', unique)
  return Object.fromEntries(
    ((data || []) as Array<{ id: string; code: string }>).map(row => [row.code, row.id]),
  )
}

/** Key the upload-document edge function files SLB documents under. */
export const getSTMIDBUploadCode = (equipment: {
  id: string
  owner_id?: string | null
  asset_number?: string | null
  serial_number?: string | null
}): string =>
  equipment.owner_id || equipment.asset_number || equipment.serial_number || equipment.id

/** True when the equipment belongs to the SLB company backed by STMIDB. */
export const isSTMIDBCompany = (companyId: string | null | undefined): boolean =>
  companyId === STMIDB_WLL_COMPANY_ID

/**
 * Resolve whether an equipment id lives in STMIDB. Checks equipment_v2 first
 * so WLL ids never hit the foreign data wrapper.
 */
export const resolveIsSTMIDBEquipment = async (equipmentId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('equipment_v2')
    .select('id')
    .eq('id', equipmentId)
    .maybeSingle()
  if (data) return false
  return isSTMIDBEquipmentId(equipmentId)
}

/**
 * Check if an equipment ID belongs to STMIDB
 */
export const isSTMIDBEquipmentId = async (equipmentId: string): Promise<boolean> => {
  try {
    const equipment = await STMIDBEquipmentService.getEquipmentById(equipmentId)
    return equipment !== null
  } catch {
    return false
  }
}

// =============================================================================
// SERVICE
// =============================================================================

export const STMIDBEquipmentService = {
  /**
   * Fetch equipment from the STMIDB customer project
   */
  async getEquipment(filters: STMIDBEquipmentFilters = {}): Promise<STMIDBEquipment[]> {
    const { data, error } = await supabase.rpc('get_stmidb_equipment', {
      p_status: filters.status || null,
      p_category: filters.category || null,
      p_search: filters.search || null,
      p_limit: filters.limit || 100,
      p_offset: filters.offset || 0,
    })

    if (error) {
      console.error('Error fetching STMIDB equipment:', error)
      throw new Error(`Failed to fetch STMIDB equipment: ${error.message}`)
    }

    return (data || []) as STMIDBEquipment[]
  },

  /**
   * Create a new SLB equipment row on the STMIDB project via the
   * create-equipment edge function. The function allocates the next
   * `SLB_NNNNN` asset number server-side (advisory-locked via
   * next_slb_asset_number()) and sets client_id to Schlumberger's UUID.
   *
   * The return shape matches STMIDB's `equipment` table row. Use
   * mapSTMIDBToWLLEquipment if you need to hand it to WLL-shaped code.
   */
  async createEquipment(params: {
    serial_number: string
    part_number?: string | null
    name?: string | null
    description?: string | null
    /** Top-level STMIDB category like "F1", "R4", "SDLA". */
    category?: string | null
    /** Specific equipment type text like "Offshore Shackle". */
    equipment_type?: string | null
    /** Human-readable identifier shown in ECM's "Owner ID" column. */
    equipment_code?: string | null
    status?: string | null
    parent_equipment_id?: string | null
    commission_date?: string | null
  }): Promise<STMIDBEquipment> {
    const response = await fetch(
      `${STMIDB_API_BASE}/functions/v1/create-equipment`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await wllAuthHeaders()) },
        body: JSON.stringify(params),
      }
    )

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(
        errorBody?.error || `Create failed with status ${response.status}`
      )
    }

    const data = await response.json()
    return data.equipment as STMIDBEquipment
  },

  /**
   * Fetch a single equipment item by ID
   */
  async getEquipmentById(id: string): Promise<STMIDBEquipment | null> {
    const { data, error } = await supabase.rpc('get_stmidb_equipment_by_id', {
      p_id: id,
    })

    if (error) {
      console.error('Error fetching STMIDB equipment by ID:', error)
      throw new Error(`Failed to fetch STMIDB equipment: ${error.message}`)
    }

    const results = data as STMIDBEquipment[]
    return results?.[0] || null
  },

  /**
   * Update equipment in the STMIDB customer project.
   * Changes sync back to the customer's database immediately.
   */
  async updateEquipment(
    id: string,
    updates: STMIDBEquipmentUpdate
  ): Promise<STMIDBEquipment> {
    const { data, error } = await supabase.rpc('update_stmidb_equipment', {
      p_id: id,
      p_updates: updates,
    })

    if (error) {
      console.error('Error updating STMIDB equipment:', error)
      throw new Error(`Failed to update STMIDB equipment: ${error.message}`)
    }

    return data as STMIDBEquipment
  },

  /**
   * Get the total count of equipment matching filters
   */
  async getCount(filters: Omit<STMIDBEquipmentFilters, 'limit' | 'offset'> = {}): Promise<number> {
    const { data, error } = await supabase.rpc('count_stmidb_equipment', {
      p_status: filters.status || null,
      p_category: filters.category || null,
      p_search: filters.search || null,
    })

    if (error) {
      console.error('Error counting STMIDB equipment:', error)
      throw new Error(`Failed to count STMIDB equipment: ${error.message}`)
    }

    return (data as number) || 0
  },

  /**
   * Create or stamp TechnipFMC WLL equipment from stmidb.service_orders.
   */
  async syncServiceOrderEquipment(): Promise<{ created: number; updated: number }> {
    const { data, error } = await supabase.rpc('sync_technipfmc_service_order_equipment')

    if (error) {
      console.error('Error syncing TechnipFMC service-order equipment:', error)
      throw new Error(`Failed to sync service-order equipment: ${error.message}`)
    }

    const { error: documentError } = await supabase.rpc('sync_technipfmc_stmidb_documents')
    if (documentError) {
      console.error('Error linking TechnipFMC STMIDB documents:', documentError)
    }

    const { error: soFileError } = await supabase.rpc('sync_technipfmc_service_order_files')
    if (soFileError) {
      console.error('Error linking TechnipFMC service-order files:', soFileError)
    }

    const result = (data ?? {}) as { created?: number; updated?: number }
    return {
      created: result.created ?? 0,
      updated: result.updated ?? 0,
    }
  },

  /**
   * TechnipFMC service orders from the stmidb project (postgres_fdw).
   */
  async getServiceOrders(): Promise<STMIDBServiceOrder[]> {
    const { data, error } = await supabase.rpc('get_stmidb_service_orders')

    if (error) {
      console.error('Error fetching STMIDB service orders:', error)
      throw new Error(`Failed to fetch STMIDB service orders: ${error.message}`)
    }

    return (data || []) as STMIDBServiceOrder[]
  },

  /**
   * TechnipFMC service-order PDFs from the stmidb `tfmc` storage bucket.
   */
  async getServiceOrderFiles(): Promise<STMIDBServiceOrderFile[]> {
    const { data, error } = await supabase.rpc('get_stmidb_service_order_files')

    if (error) {
      console.error('Error fetching STMIDB service-order files:', error)
      throw new Error(`Failed to fetch service-order files: ${error.message}`)
    }

    return (data || []) as STMIDBServiceOrderFile[]
  },

  /**
   * Get all unique categories from STMIDB equipment
   */
  async getCategories(): Promise<string[]> {
    const equipment = await this.getEquipment({ limit: 10000 })
    const categories = [
      ...new Set(
        equipment
          .map(e => e.category)
          .filter((c): c is string => c !== null && c !== '')
      ),
    ]
    return categories.sort()
  },

  /**
   * Get all unique statuses from STMIDB equipment
   */
  async getStatuses(): Promise<string[]> {
    const equipment = await this.getEquipment({ limit: 10000 })
    const statuses = [
      ...new Set(
        equipment
          .map(e => e.status)
          .filter((s): s is string => s !== null && s !== '')
      ),
    ]
    return statuses.sort()
  },

  // ===========================================================================
  // DOCUMENTS
  // ===========================================================================

  /**
   * Get documents for an STMIDB equipment.
   *
   * Filters out `.keep` placeholder rows that STMIDB keeps around to preserve
   * empty document folders in the `documentation` bucket. These aren't real
   * documents and must never surface in the ECM UI — they'd show up as
   * zero-byte entries with no openable content.
   */
  async getDocuments(equipmentId: string): Promise<STMIDBDocument[]> {
    const { data, error } = await supabase.rpc('get_stmidb_equipment_documents', {
      p_equipment_id: equipmentId,
    })

    if (error) {
      console.error('Error fetching STMIDB documents:', error)
      return []
    }

    return (data || []).filter((doc: STMIDBDocument) => !isSTMIDBPlaceholderDocument(doc))
  },

  /**
   * Upload a document to the STMIDB project via the upload-document edge
   * function. File ends up in the `documentation` bucket at
   * `schlumberger/<equipment_code>/<document_type>/<timestamp>_<filename>`
   * and a row is inserted into `public.documents`.
   *
   * `documentType` must already be an STMIDB-side enum value
   * (co, ndt, cert, coc, man, sr, 5_year). Use mapWLLDocumentTypeToSTMIDB
   * when translating from WLL document types.
   */
  async uploadDocument(params: {
    equipmentId: string
    equipmentCode: string
    documentType: string
    file: File
  }): Promise<STMIDBDocument> {
    const form = new FormData()
    form.append('equipment_id', params.equipmentId)
    form.append('equipment_code', params.equipmentCode)
    form.append('document_type', params.documentType)
    form.append('file', params.file)

    const response = await fetch(`${STMIDB_API_BASE}/functions/v1/upload-document`, {
      method: 'POST',
      headers: await wllAuthHeaders(),
      body: form,
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(
        errorBody?.error || `Upload failed with status ${response.status}`
      )
    }

    const data = await response.json()
    return data.document as STMIDBDocument
  },

  /**
   * Delete a document from the STMIDB project via the delete-document edge
   * function. Removes the storage object (best-effort) and the row in
   * `public.documents`.
   */
  async deleteDocument(documentId: string): Promise<boolean> {
    const response = await fetch(`${STMIDB_API_BASE}/functions/v1/delete-document`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await wllAuthHeaders()) },
      body: JSON.stringify({ document_id: documentId }),
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(
        errorBody?.error || `Delete failed with status ${response.status}`
      )
    }

    const data = await response.json()
    return Boolean(data?.success)
  },

  /**
   * Delete an SLB equipment row on the STMIDB project via the
   * delete-equipment edge function. The edge function cascades through
   * `documents` (storage + rows), `inspections`, and `control_info` before
   * removing the `equipment` row, so callers do not need to pre-clean
   * dependents.
   *
   * Returns the number of documents that were cleaned up alongside the row.
   */
  async deleteEquipment(
    equipmentId: string
  ): Promise<{ success: boolean; documentsDeleted: number }> {
    const response = await fetch(
      `${STMIDB_API_BASE}/functions/v1/delete-equipment`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await wllAuthHeaders()) },
        body: JSON.stringify({ equipment_id: equipmentId }),
      }
    )

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(
        errorBody?.error || `Delete failed with status ${response.status}`
      )
    }

    const data = await response.json()
    return {
      success: Boolean(data?.success),
      documentsDeleted: Number(data?.documents_deleted) || 0,
    }
  },

  // ===========================================================================
  // SUB-EQUIPMENT
  // ===========================================================================

  /**
   * Get sub-equipment for a given parent equipment from STMIDB
   */
  async getSubEquipment(parentId: string): Promise<STMIDBEquipment[]> {
    const { data, error } = await supabase.rpc('get_stmidb_sub_equipment', {
      p_parent_id: parentId,
    })

    if (error) {
      console.error('Error fetching STMIDB sub-equipment:', error)
      return []
    }

    return data || []
  },

  /**
   * Report forms (Rapportering): search all SLB equipment by SN / EQ /
   * asset number. Unlike getEquipment, attached sub-equipment is included.
   */
  async searchReportEquipment(search: string, limit = 50): Promise<STMIDBReportEquipmentHit[]> {
    const { data, error } = await supabase.rpc('search_stmidb_report_equipment', {
      p_search: search,
      p_limit: limit,
    })

    if (error) {
      console.error('Error searching STMIDB report equipment:', error)
      throw new Error(`Failed to search STMIDB equipment: ${error.message}`)
    }

    return (data || []) as STMIDBReportEquipmentHit[]
  },

  /**
   * Unattached R1/R3/R4/WL equipment that can become sub-equipment of
   * `parentId`. Mirrors the STMIDB app's attach search; the parent's own
   * ancestors are excluded server-side so no cycles can be created.
   */
  async searchAttachableEquipment(
    parentId: string,
    search?: string,
    limit = 50
  ): Promise<STMIDBAttachableEquipment[]> {
    const { data, error } = await supabase.rpc('search_stmidb_attachable_equipment', {
      p_parent_id: parentId,
      p_search: search?.trim() || null,
      p_limit: limit,
    })

    if (error) {
      console.error('Error searching attachable STMIDB equipment:', error)
      throw new Error(`Failed to search STMIDB equipment: ${error.message}`)
    }

    return data || []
  },

  /**
   * Attach (`parentId` set) or detach (`parentId` null) a sub-equipment row by
   * writing STMIDB's `parent_equipment_id`.
   */
  async setParentEquipment(childId: string, parentId: string | null): Promise<void> {
    const { error } = await supabase.rpc('set_stmidb_parent_equipment', {
      p_child_id: childId,
      p_parent_id: parentId,
    })

    if (error) {
      console.error('Error setting STMIDB parent equipment:', error)
      throw new Error(error.message)
    }
  },

  /**
   * Set an SLB item's NDT Dato spec. The database trigger on
   * external_equipment_specifications then sets STMIDB's last_ndt_date to this
   * date and next_ndt_date (48M DUE) to date + 48 months.
   */
  async setNDTDate(equipmentId: string, ndtDate: string): Promise<void> {
    const { error } = await supabase.rpc('set_stmidb_ndt_date', {
      p_equipment_id: equipmentId,
      p_ndt_date: ndtDate,
    })

    if (error) {
      console.error('Error setting STMIDB NDT date:', error)
      throw new Error(error.message)
    }
  },

  /** Every STMIDB row that has a parent — used for the table's sub-equipment subtitles. */
  async getAllChildEquipment(): Promise<STMIDBChildEquipment[]> {
    // Supabase's API caps every response at 1000 rows, so page with range().
    const pageSize = 1000
    const rows: STMIDBChildEquipment[] = []
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await supabase
        .rpc('get_stmidb_child_equipment')
        .range(from, from + pageSize - 1)

      if (error) {
        console.error('Error fetching STMIDB child equipment:', error)
        return rows
      }

      const page = (data || []) as STMIDBChildEquipment[]
      rows.push(...page)
      if (page.length < pageSize) return rows
    }
  },

  // ===========================================================================
  // EXTERNAL EQUIPMENT SPECIFICATIONS
  // These specs are stored in the app for STMIDB equipment (since STMIDB has no specs)
  // ===========================================================================

  /**
   * Get specifications for an external (STMIDB) equipment item
   */
  async getExternalEquipmentSpecs(externalEquipmentId: string): Promise<ExternalEquipmentSpec[]> {
    const { data, error } = await supabase.rpc('get_external_equipment_specifications', {
      p_external_equipment_id: externalEquipmentId,
      p_source: 'stmidb',
    })

    if (error) {
      console.error('Error fetching external equipment specs:', error)
      return []
    }

    return data || []
  },

  /**
   * Save/update a specification for external equipment
   */
  async saveExternalEquipmentSpec(params: {
    externalEquipmentId: string
    specKey: string
    templateId: string
    valueText?: string | null
    valueNumeric?: number | null
    valueInteger?: number | null
    valueDate?: string | null
    valueBoolean?: boolean | null
    valueReferenceId?: string | null
    unitId?: string | null
  }): Promise<string | null> {
    const { data, error } = await supabase.rpc('upsert_external_equipment_specification', {
      p_external_equipment_id: params.externalEquipmentId,
      p_source: 'stmidb',
      p_spec_key: params.specKey,
      p_template_id: params.templateId,
      p_value_text: params.valueText ?? null,
      p_value_numeric: params.valueNumeric ?? null,
      p_value_integer: params.valueInteger ?? null,
      p_value_date: params.valueDate ?? null,
      p_value_boolean: params.valueBoolean ?? null,
      p_value_reference_id: params.valueReferenceId ?? null,
      p_unit_id: params.unitId ?? null,
    })

    if (error) {
      console.error('Error saving external equipment spec:', error)
      throw new Error(`Failed to save specification: ${error.message}`)
    }

    return data
  },

  /**
   * Delete a specification for external equipment
   */
  async deleteExternalEquipmentSpec(
    externalEquipmentId: string,
    specKey: string
  ): Promise<boolean> {
    const { data, error } = await supabase.rpc('delete_external_equipment_specification', {
      p_external_equipment_id: externalEquipmentId,
      p_source: 'stmidb',
      p_spec_key: specKey,
    })

    if (error) {
      console.error('Error deleting external equipment spec:', error)
      return false
    }

    return data || false
  },

  /**
   * Bulk save multiple specifications for external equipment
   */
  async saveExternalEquipmentSpecsBulk(
    externalEquipmentId: string,
    specs: Array<{
      specKey: string
      templateId: string
      valueText?: string | null
      valueNumeric?: number | null
      valueInteger?: number | null
      valueDate?: string | null
      valueBoolean?: boolean | null
      valueReferenceId?: string | null
      unitId?: string | null
    }>
  ): Promise<{ success: boolean; savedCount: number; errors: string[] }> {
    const errors: string[] = []
    let savedCount = 0

    for (const spec of specs) {
      try {
        await this.saveExternalEquipmentSpec({
          externalEquipmentId,
          ...spec,
        })
        savedCount++
      } catch (error) {
        errors.push(
          `Failed to save ${spec.specKey}: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }
    }

    return {
      success: errors.length === 0,
      savedCount,
      errors,
    }
  },
}

// =============================================================================
// TYPE DEFINITIONS FOR EXTERNAL SPECS
// =============================================================================

export interface ExternalEquipmentSpec {
  id: string
  spec_key: string
  template_id: string | null
  value_text: string | null
  value_numeric: number | null
  value_integer: number | null
  value_date: string | null
  value_boolean: boolean | null
  value_reference_id: string | null
  unit_id: string | null
  label_en: string | null
  label_no: string | null
  data_type: string | null
  group_name: string | null
  display_order: number | null
  is_required: boolean
}

export default STMIDBEquipmentService
